// staff-app/src/scripts/notifications/staffNotifications.js

import app, {
  auth,
} from "@/firebase";

import {
  onAuthStateChanged,
} from "firebase/auth";

import {
  getFunctions,
  httpsCallable,
} from "firebase/functions";

import {
  getMessaging,
  getToken,
  isSupported,
  onMessage,
} from "firebase/messaging";

const REGION =
  "us-central1";

const VAPID_KEY =
  String(
    process.env
      .VUE_APP_FIREBASE_VAPID_KEY ||
      ""
  ).trim();

const CLICKED_MODULE_PARAM =
  "coredeckNotificationModule";

const STAFF_SESSION_STORAGE_KEY =
  "coredeck_staff_session";

const STAFF_SESSION_READY_EVENT =
  "coredeck-staff-session-ready";

const STAFF_SESSION_CLEARED_EVENT =
  "coredeck-staff-session-cleared";

const MODULES = {
  HOLIDAY:
    "holiday",

  TRAINING:
    "training",
};

const functions =
  getFunctions(
    app,
    REGION
  );

const getNotificationsCallable =
  httpsCallable(
    functions,
    "staffNotificationsGet"
  );

const markReadCallable =
  httpsCallable(
    functions,
    "staffNotificationsMarkRead"
  );

const registerPushTokenCallable =
  httpsCallable(
    functions,
    "staffRegisterPushToken"
  );

const unregisterPushTokenCallable =
  httpsCallable(
    functions,
    "staffUnregisterPushToken"
  );

let installed =
  false;

let currentUid =
  "";

let currentSession =
  null;

let notifications =
  [];

let currentPushToken =
  "";

let pollingTimer =
  null;

let uiObserver =
  null;

let uiFrame =
  null;

let notificationPromptDismissed =
  false;

let messagingInstance =
  null;

let unsubscribeAuth =
  null;

let unsubscribeForeground =
  null;

/* =========================================================
   Helpers
   ========================================================= */

function clean(value) {
  return String(
    value ?? ""
  ).trim();
}

function escapeHtml(value) {
  return String(
    value ?? ""
  )
    .replace(
      /&/g,
      "&amp;"
    )
    .replace(
      /</g,
      "&lt;"
    )
    .replace(
      />/g,
      "&gt;"
    )
    .replace(
      /"/g,
      "&quot;"
    )
    .replace(
      /'/g,
      "&#039;"
    );
}

function vapidConfigured() {
  return Boolean(
    VAPID_KEY &&
    !VAPID_KEY.includes(
      "PASTE_"
    ) &&
    !VAPID_KEY.includes(
      "YOUR_"
    )
  );
}

function toast(
  message,
  classes = ""
) {
  const text =
    clean(
      message
    );

  if (!text) {
    return;
  }

  if (
    window.M?.toast
  ) {
    window.M.toast({
      html:
        escapeHtml(
          text
        ),

      classes,
    });

    return;
  }

  console.info(
    "[Staff Notifications]",
    text
  );
}

function browserPermission() {
  if (
    typeof window ===
      "undefined" ||
    !(
      "Notification" in
      window
    )
  ) {
    return "unsupported";
  }

  return Notification
    .permission;
}

/* =========================================================
   Staff session
   ========================================================= */

function readCachedStaffSession() {
  if (
    !currentUid
  ) {
    return null;
  }

  try {
    const raw =
      window
        .sessionStorage
        .getItem(
          STAFF_SESSION_STORAGE_KEY
        );

    if (!raw) {
      return null;
    }

    const session =
      JSON.parse(
        raw
      );

    if (
      clean(
        session?.uid
      ) !==
      currentUid
    ) {
      return null;
    }

    if (
      !clean(
        session?.venueId
      ) ||

      !clean(
        session?.staffId
      )
    ) {
      return null;
    }

    return session;
  } catch {
    return null;
  }
}

function sessionIsReady() {
  const cached =
    readCachedStaffSession();

  if (!cached) {
    currentSession =
      null;

    return false;
  }

  currentSession =
    cached;

  return true;
}

/* =========================================================
   Notification click URL
   ========================================================= */

function clickedNotificationModule() {
  try {
    const url =
      new URL(
        window.location.href
      );

    const module =
      clean(
        url
          .searchParams
          .get(
            CLICKED_MODULE_PARAM
          )
      );

    return Object
      .values(
        MODULES
      )
      .includes(
        module
      )
      ? module
      : "";
  } catch {
    return "";
  }
}

function clearClickedNotificationModule() {
  try {
    const url =
      new URL(
        window.location.href
      );

    if (
      !url.searchParams.has(
        CLICKED_MODULE_PARAM
      )
    ) {
      return;
    }

    url.searchParams.delete(
      CLICKED_MODULE_PARAM
    );

    window.history.replaceState(
      window.history.state,
      "",
      `${url.pathname}${url.search}${url.hash}`
    );
  } catch {
    // no-op
  }
}

/* =========================================================
   Badges
   ========================================================= */

function notificationsForModule(
  module
) {
  return notifications
    .filter(
      (
        notification
      ) =>
        notification.read !==
          true &&

        clean(
          notification.module
        ) ===
          module
    );
}

function countForModule(
  module
) {
  return notificationsForModule(
    module
  ).length;
}

function badgeColour(
  module
) {
  const items =
    notificationsForModule(
      module
    );

  if (
    items.some(
      (
        item
      ) =>
        [
          "declined",
          "rejected",
          "overdue",
        ].includes(
          clean(
            item.status
          )
        )
    )
  ) {
    return [
      "red",
      "darken-2",
    ];
  }

  if (
    items.some(
      (
        item
      ) =>
        [
          "due",
          "amendment",
          "attention",
        ].includes(
          clean(
            item.status
          )
        )
    )
  ) {
    return [
      "orange",
      "darken-2",
    ];
  }

  return [
    "green",
    "darken-2",
  ];
}

function existingBadge(
  button,
  module
) {
  const matches =
    Array.from(
      button.querySelectorAll(
        `.staff-notification-badge[data-staff-notification-module="${module}"]`
      )
    );

  matches
    .slice(
      1
    )
    .forEach(
      (
        badge
      ) =>
        badge.remove()
    );

  return matches[
    0
  ] ||
    null;
}

function updateBadge(
  button,
  module
) {
  if (!button) {
    return;
  }

  const count =
    countForModule(
      module
    );

  let badge =
    existingBadge(
      button,
      module
    );

  if (!count) {
    badge?.remove();

    return;
  }

  if (!badge) {
    badge =
      document.createElement(
        "span"
      );

    badge.dataset
      .staffNotificationModule =
      module;

    badge.setAttribute(
      "data-badge-caption",
      ""
    );

    badge.style.marginLeft =
      "8px";

    badge.style.float =
      "none";

    badge.style.display =
      "inline-block";

    button.appendChild(
      badge
    );
  }

  badge.className = [
    "new",
    "badge",
    "staff-notification-badge",
    ...badgeColour(
      module
    ),
  ].join(" ");

  badge.textContent =
    String(
      count
    );

  badge.dataset
    .staffNotificationModule =
    module;

  badge.setAttribute(
    "data-badge-caption",
    ""
  );
}

function syncBadges() {
  document
    .querySelectorAll(
      ".training-btn"
    )
    .forEach(
      (
        button
      ) => {
        updateBadge(
          button,
          MODULES.TRAINING
        );
      }
    );

  document
    .querySelectorAll(
      ".holiday-request-btn"
    )
    .forEach(
      (
        button
      ) => {
        updateBadge(
          button,
          MODULES.HOLIDAY
        );
      }
    );
}

/* =========================================================
   Browser permission controls / first-login prompt
   ========================================================= */

function permissionButtonState() {
  if (
    !vapidConfigured()
  ) {
    return {
      label:
        "Notifications Unavailable",

      icon:
        "notifications_off",

      classes:
        "btn blue-grey darken-2",

      title:
        "Firebase Web Push is not configured.",
    };
  }

  const permission =
    browserPermission();

  if (
    permission ===
    "granted"
  ) {
    return {
      label:
        "Notifications On",

      icon:
        "notifications_active",

      classes:
        "btn green darken-2",

      title:
        "Browser notifications are allowed.",
    };
  }

  if (
    permission ===
    "denied"
  ) {
    return {
      label:
        "Notifications Blocked",

      icon:
        "notifications_off",

      classes:
        "btn red darken-2",

      title:
        "Notifications are blocked in this browser.",
    };
  }

  if (
    permission ===
    "default"
  ) {
    return {
      label:
        "Enable Notifications",

      icon:
        "notifications_active",

      classes:
        "btn orange darken-2",

      title:
        "Enable browser notifications.",
    };
  }

  return {
    label:
      "Notifications Unsupported",

    icon:
      "notifications_off",

    classes:
      "btn blue-grey darken-2",

    title:
      "This browser does not support notifications.",
  };
}

function createPermissionButton() {
  const button =
    document.createElement(
      "button"
    );

  button.type =
    "button";

  button.dataset
    .staffNotificationPermission =
    "true";

  button.addEventListener(
    "click",

    async (
      event
    ) => {
      event.preventDefault();

      button.disabled =
        true;

      try {
        await handlePermissionButtonClick();
      } finally {
        button.disabled =
          false;

        scheduleUiSync();
      }
    }
  );

  return button;
}

function syncPermissionButton() {
  document
    .querySelectorAll(
      ".top-actions"
    )
    .forEach(
      (
        target
      ) => {
        let button =
          target.querySelector(
            "[data-staff-notification-permission]"
          );

        if (!button) {
          button =
            createPermissionButton();

          const signOut =
            target.querySelector(
              ".signout-link"
            );

          if (
            signOut
          ) {
            target.insertBefore(
              button,
              signOut
            );
          } else {
            target.appendChild(
              button
            );
          }
        }

        const state =
          permissionButtonState();

        button.className =
          state.classes;

        button.title =
          state.title;

        button.innerHTML = `
          <i class="material-icons left">${state.icon}</i>
          ${escapeHtml(state.label)}
        `;
      }
    );
}

function removeStaffNotificationPrompt() {
  document
    .querySelectorAll(
      "[data-staff-notification-onboarding]"
    )
    .forEach(
      (
        element
      ) =>
        element.remove()
    );
}

function showStaffNotificationPrompt() {
  if (
    !currentUid ||
    !sessionIsReady() ||
    notificationPromptDismissed ||
    !vapidConfigured() ||
    browserPermission() !==
      "default"
  ) {
    removeStaffNotificationPrompt();

    return;
  }

  if (
    document.querySelector(
      "[data-staff-notification-onboarding]"
    )
  ) {
    return;
  }

  const overlay =
    document.createElement(
      "div"
    );

  overlay.dataset
    .staffNotificationOnboarding =
    "true";

  overlay.style.position =
    "fixed";

  overlay.style.inset =
    "0";

  overlay.style.zIndex =
    "10000";

  overlay.style.display =
    "flex";

  overlay.style.alignItems =
    "center";

  overlay.style.justifyContent =
    "center";

  overlay.style.padding =
    "20px";

  overlay.style.background =
    "rgba(0,0,0,0.48)";

  const card =
    document.createElement(
      "div"
    );

  card.className =
    "card white";

  card.style.width =
    "min(520px, 100%)";

  card.style.margin =
    "0";

  card.style.borderRadius =
    "12px";

  card.innerHTML = `
    <div class="card-content">
      <span class="card-title" style="font-weight:700;display:flex;align-items:center;gap:10px;">
        <i class="material-icons orange-text text-darken-2">notifications_active</i>
        Enable Staff notifications
      </span>

      <p style="margin-top:12px;line-height:1.6;">
        Allow notifications so CoreDeck can tell you when your holiday request is approved or declined and when training is due or overdue.
      </p>
    </div>

    <div class="card-action" style="display:flex;justify-content:flex-end;gap:10px;flex-wrap:wrap;">
      <button
        type="button"
        class="btn-flat"
        data-staff-notification-not-now
      >
        Not now
      </button>

      <button
        type="button"
        class="btn orange darken-2 waves-effect waves-light"
        data-staff-notification-enable
      >
        <i class="material-icons left">notifications_active</i>
        Enable Notifications
      </button>
    </div>
  `;

  overlay.appendChild(
    card
  );

  document.body.appendChild(
    overlay
  );

  card
    .querySelector(
      "[data-staff-notification-not-now]"
    )
    ?.addEventListener(
      "click",
      () => {
        notificationPromptDismissed =
          true;

        overlay.remove();
      }
    );

  card
    .querySelector(
      "[data-staff-notification-enable]"
    )
    ?.addEventListener(
      "click",
      async () => {
        await enableNotifications();

        scheduleUiSync();
      }
    );
}

function syncStaffNotificationPrompt() {
  if (
    currentUid &&
    sessionIsReady() &&
    browserPermission() ===
      "default"
  ) {
    showStaffNotificationPrompt();

    return;
  }

  removeStaffNotificationPrompt();
}

/* =========================================================
   UI
   ========================================================= */

function syncUi() {
  syncBadges();

  syncPermissionButton();

  syncStaffNotificationPrompt();
}

function scheduleUiSync() {
  if (
    uiFrame
  ) {
    cancelAnimationFrame(
      uiFrame
    );
  }

  uiFrame =
    requestAnimationFrame(
      () => {
        uiFrame =
          null;

        syncUi();
      }
    );
}

/* =========================================================
   Notification list
   ========================================================= */

async function refreshNotifications() {
  if (
    !currentUid ||
    !sessionIsReady()
  ) {
    notifications =
      [];

    scheduleUiSync();

    return false;
  }

  try {
    const result =
      await getNotificationsCallable();

    const data =
      result?.data ||
      {};

    notifications =
      Array.isArray(
        data.notifications
      )
        ? data.notifications
        : [];

    scheduleUiSync();

    const clickedModule =
      clickedNotificationModule();

    if (
      clickedModule
    ) {
      await markModuleRead(
        clickedModule
      );

      clearClickedNotificationModule();
    }

    return true;
  } catch (error) {
    console.warn(
      "[Staff Notifications] Notification load failed:",
      error?.message ||
        error
    );

    return false;
  }
}

async function markModuleRead(
  module
) {
  if (
    !currentUid ||
    !module ||
    !sessionIsReady()
  ) {
    return;
  }

  try {
    await markReadCallable({
      module,
    });

    notifications =
      notifications.filter(
        (
          item
        ) =>
          clean(
            item.module
          ) !==
          module
      );

    scheduleUiSync();
  } catch (error) {
    console.warn(
      "[Staff Notifications] Mark read failed:",
      error?.message ||
        error
    );
  }
}

function onDocumentClick(
  event
) {
  if (
    event.target
      ?.closest?.(
        ".training-btn"
      )
  ) {
    markModuleRead(
      MODULES.TRAINING
    );

    return;
  }

  if (
    event.target
      ?.closest?.(
        ".holiday-request-btn"
      )
  ) {
    markModuleRead(
      MODULES.HOLIDAY
    );
  }
}

/* =========================================================
   Firebase Messaging
   ========================================================= */

async function getCoreMessaging() {
  if (
    messagingInstance
  ) {
    return messagingInstance;
  }

  try {
    if (
      !(
        await isSupported()
      )
    ) {
      return null;
    }
  } catch {
    return null;
  }

  messagingInstance =
    getMessaging(
      app
    );

  return messagingInstance;
}

function installForegroundListener(
  instance
) {
  if (
    unsubscribeForeground
  ) {
    return;
  }

  unsubscribeForeground =
    onMessage(
      instance,

      async (
        payload
      ) => {
        const data =
          payload?.data ||
          {};

        let classes =
          "green darken-2";

        if (
          [
            "declined",
            "rejected",
            "overdue",
          ].includes(
            data.status
          )
        ) {
          classes =
            "red darken-2";
        } else if (
          [
            "due",
            "amendment",
            "attention",
          ].includes(
            data.status
          )
        ) {
          classes =
            "orange darken-2";
        }

        toast(
          data.body
            ? `${data.title || "CoreDeck"}: ${data.body}`
            : data.title ||
                "CoreDeck notification",

          classes
        );

        await refreshNotifications();
      }
    );
}

async function registerCurrentDevice() {
  if (
    !currentUid ||
    !sessionIsReady() ||
    !vapidConfigured() ||
    !(
      "serviceWorker" in
      navigator
    ) ||
    browserPermission() !==
      "granted"
  ) {
    return false;
  }

  const instance =
    await getCoreMessaging();

  if (!instance) {
    return false;
  }

  const serviceWorkerRegistration =
    await navigator
      .serviceWorker
      .register(
        "/firebase-messaging-sw.js"
      );

  const token =
    await getToken(
      instance,
      {
        vapidKey:
          VAPID_KEY,

        serviceWorkerRegistration,
      }
    );

  if (!token) {
    return false;
  }

  currentPushToken =
    token;

  await registerPushTokenCallable({
    token,
  });

  installForegroundListener(
    instance
  );

  return true;
}

async function unregisterCurrentDevice() {
  if (
    !currentUid ||
    !currentPushToken
  ) {
    return false;
  }

  try {
    await unregisterPushTokenCallable({
      token:
        currentPushToken,
    });

    currentPushToken =
      "";

    return true;
  } catch (error) {
    console.warn(
      "[Staff Notifications] Push unregister failed:",
      error?.message ||
        error
    );

    return false;
  }
}

/* =========================================================
   Browser permission
   ========================================================= */

async function enableNotifications() {
  if (
    !vapidConfigured()
  ) {
    toast(
      "Staff App push notifications have not been configured.",
      "orange darken-2"
    );

    return false;
  }

  if (
    browserPermission() ===
    "unsupported"
  ) {
    toast(
      "This browser does not support device notifications.",
      "orange darken-2"
    );

    return false;
  }

  if (
    browserPermission() ===
    "denied"
  ) {
    toast(
      "Notifications are blocked in your browser. Allow notifications for this site in the browser's site settings.",
      "red darken-2"
    );

    return false;
  }

  let permission =
    browserPermission();

  if (
    permission ===
    "default"
  ) {
    permission =
      await Notification
        .requestPermission();
  }

  scheduleUiSync();

  if (
    permission !==
    "granted"
  ) {
    toast(
      "Device notifications were not enabled.",
      "orange darken-2"
    );

    return false;
  }

  if (
    !sessionIsReady()
  ) {
    toast(
      "Finish signing in before enabling notifications.",
      "orange darken-2"
    );

    return false;
  }

  try {
    const success =
      await registerCurrentDevice();

    toast(
      success
        ? "CoreDeck Staff notifications are on."
        : "This device could not be registered for notifications.",

      success
        ? "green darken-2"
        : "orange darken-2"
    );

    return success;
  } catch (error) {
    console.error(
      "[Staff Notifications] FCM registration failed:",
      error
    );

    toast(
      "Could not enable Staff App notifications.",
      "red darken-2"
    );

    return false;
  }
}

async function handlePermissionButtonClick() {
  const permission =
    browserPermission();

  if (
    permission ===
    "default"
  ) {
    await enableNotifications();

    return;
  }

  if (
    permission ===
    "granted"
  ) {
    if (
      !sessionIsReady()
    ) {
      toast(
        "Finish signing in before registering this device.",
        "orange darken-2"
      );

      return;
    }

    try {
      const success =
        await registerCurrentDevice();

      toast(
        success
          ? "Notifications are on for this browser."
          : "Notifications are allowed, but this device could not be registered.",

        success
          ? "green darken-2"
          : "orange darken-2"
      );
    } catch (error) {
      console.error(
        "[Staff Notifications] Device refresh failed:",
        error
      );

      toast(
        "Notifications are allowed, but CoreDeck could not refresh this device registration.",
        "red darken-2"
      );
    }

    return;
  }

  if (
    permission ===
    "denied"
  ) {
    toast(
      "Notifications are blocked by the browser. Open this site's permissions and change Notifications to Allow.",
      "red darken-2"
    );

    return;
  }

  toast(
    "This browser does not support web notifications.",
    "orange darken-2"
  );
}

/* =========================================================
   Valid Staff session activation
   ========================================================= */

async function activateForCurrentStaffSession() {
  if (
    !currentUid ||
    !sessionIsReady()
  ) {
    return false;
  }

  await refreshNotifications();

  if (
    browserPermission() ===
    "granted"
  ) {
    try {
      await registerCurrentDevice();
    } catch (error) {
      console.warn(
        "[Staff Notifications] Existing device registration refresh failed:",
        error?.message ||
          error
      );
    }
  }

  scheduleUiSync();

  return true;
}

async function onStaffSessionReady(
  event
) {
  const authUid =
    clean(
      auth.currentUser
        ?.uid
    );

  const eventUid =
    clean(
      event?.detail
        ?.uid
    );

  if (
    !authUid
  ) {
    return;
  }

  if (
    eventUid &&
    eventUid !==
      authUid
  ) {
    return;
  }

  currentUid =
    authUid;

  await activateForCurrentStaffSession();
}

function onStaffSessionCleared() {
  currentSession =
    null;

  notifications =
    [];

  currentPushToken =
    "";

  scheduleUiSync();
}

/* =========================================================
   Polling
   ========================================================= */

function stopPolling() {
  if (
    pollingTimer
  ) {
    window.clearInterval(
      pollingTimer
    );
  }

  pollingTimer =
    null;
}

function startPolling() {
  stopPolling();

  pollingTimer =
    window.setInterval(
      async () => {
        if (
          document
            .visibilityState !==
          "visible"
        ) {
          return;
        }

        if (
          !sessionIsReady()
        ) {
          return;
        }

        await refreshNotifications();
      },

      60000
    );
}

async function onVisibilityChange() {
  if (
    document.visibilityState !==
    "visible"
  ) {
    return;
  }

  if (
    !sessionIsReady()
  ) {
    return;
  }

  await activateForCurrentStaffSession();
}

/* =========================================================
   Firebase Auth lifecycle
   ========================================================= */

async function startForAuthUser(
  user
) {
  notificationPromptDismissed =
    false;

  currentUid =
    clean(
      user?.uid
    );

  currentSession =
    null;

  notifications =
    [];

  if (
    !currentUid
  ) {
    scheduleUiSync();

    return;
  }

  startPolling();

  scheduleUiSync();

  if (
    sessionIsReady()
  ) {
    await activateForCurrentStaffSession();
  }
}

function stopForAuthUser() {
  notificationPromptDismissed =
    false;

  currentUid =
    "";

  currentSession =
    null;

  currentPushToken =
    "";

  notifications =
    [];

  stopPolling();

  scheduleUiSync();
}

/* =========================================================
   Install
   ========================================================= */

export function installStaffNotifications() {
  if (
    installed
  ) {
    return;
  }

  installed =
    true;

  document.addEventListener(
    "click",
    onDocumentClick,
    true
  );

  document.addEventListener(
    "visibilitychange",
    onVisibilityChange
  );

  window.addEventListener(
    STAFF_SESSION_READY_EVENT,
    onStaffSessionReady
  );

  window.addEventListener(
    STAFF_SESSION_CLEARED_EVENT,
    onStaffSessionCleared
  );

  if (
    typeof MutationObserver !==
    "undefined"
  ) {
    uiObserver =
      new MutationObserver(
        () => {
          scheduleUiSync();
        }
      );

    uiObserver.observe(
      document.documentElement,
      {
        subtree:
          true,

        childList:
          true,
      }
    );
  }

  unsubscribeAuth =
    onAuthStateChanged(
      auth,

      async (
        user
      ) => {
        if (user) {
          await startForAuthUser(
            user
          );
        } else {
          stopForAuthUser();
        }
      }
    );

  scheduleUiSync();
}

export function uninstallStaffNotifications() {
  if (
    !installed
  ) {
    return;
  }

  installed =
    false;

  document.removeEventListener(
    "click",
    onDocumentClick,
    true
  );

  document.removeEventListener(
    "visibilitychange",
    onVisibilityChange
  );

  window.removeEventListener(
    STAFF_SESSION_READY_EVENT,
    onStaffSessionReady
  );

  window.removeEventListener(
    STAFF_SESSION_CLEARED_EVENT,
    onStaffSessionCleared
  );

  uiObserver
    ?.disconnect();

  uiObserver =
    null;

  stopPolling();

  if (
    unsubscribeAuth
  ) {
    unsubscribeAuth();
  }

  if (
    unsubscribeForeground
  ) {
    unsubscribeForeground();
  }

  unsubscribeAuth =
    null;

  unsubscribeForeground =
    null;

  stopForAuthUser();
}

export {
  enableNotifications,
  unregisterCurrentDevice,
  markModuleRead,
  refreshNotifications,
};

installStaffNotifications();